/**
 * The prototype's icon set, ported one path at a time from `design/personal-car-app/index.html`
 * (`const ICON`). Line icons, 1.8px stroke, round caps — the weight fixed in docs/22 §3.1;
 * the three navigation glyphs (back, close, plus) use 2.1–2.2 because they read as controls,
 * not as illustrations.
 *
 * Every icon is decorative: it is always accompanied by a label or sits inside a control that
 * carries its own accessible name, so `aria-hidden` is correct on all of them.
 */
import type { SVGProps } from 'react';

/**
 * `strokeWidth` is narrowed to a number: SVG allows a string there, but every weight in this set
 * is one of three fixed values, and letting a string through would make «1.8» and 1.8 two
 * different things to compare against.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, 'strokeWidth'> & {
  size?: number;
  strokeWidth?: number;
};

function Icon({ size = 20, strokeWidth = 1.8, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function CarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
      <rect x="3" y="11" width="18" height="6" rx="2" />
      <circle cx="7.5" cy="17.5" r="1.6" />
      <circle cx="16.5" cy="17.5" r="1.6" />
    </Icon>
  );
}

export function FuelIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" />
      <path d="M4 11h9" />
      <path d="M15 8l3 2v6a1.5 1.5 0 0 0 3 0V9l-3-3" />
      <path d="M2 21h14" />
    </Icon>
  );
}

export function CardIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.4" />
      <path d="M2.5 10h19" />
      <path d="M6 14.5h4" />
    </Icon>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 10a6 6 0 1 1 12 0c0 3 1 4.5 2 5.5H4c1-1 2-2.5 2-5.5z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </Icon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.5 20c1.2-3.6 4-5.4 7.5-5.4S18.8 16.4 20 20" />
    </Icon>
  );
}

export function HistoryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l4 2" />
    </Icon>
  );
}

export function ReceiptIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3h12v18l-2.5-1.5L13 21l-2.5-1.5L8 21l-2-1.5V3z" />
      <path d="M8.5 8h7M8.5 12h7M8.5 16h4" />
    </Icon>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5h16v10H9l-4 4V5z" />
    </Icon>
  );
}

export function LogoutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M15 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
      <path d="M11 12h9m0 0l-3-3m3 3l-3 3" />
    </Icon>
  );
}

export function BackIcon(props: IconProps) {
  return (
    <Icon size={18} strokeWidth={2.1} {...props}>
      <path d="M15 5l-7 7 7 7" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon size={18} strokeWidth={2.1} {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <Icon size={18} strokeWidth={2.2} {...props}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Icon size={18} {...props}>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m1 0l-.8 12.1a1 1 0 0 1-1 .9H8.8a1 1 0 0 1-1-.9L7 7" />
    </Icon>
  );
}

export function ChevronIcon(props: IconProps) {
  return (
    <Icon size={16} strokeWidth={2.1} {...props}>
      <path d="M9 6l6 6-6 6" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon strokeWidth={2} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.3l2.4 2.4 4.6-5.4" />
    </Icon>
  );
}

/**
 * СБП — телефон с платёжным кодом.
 *
 * Не логотип системы: он цветной, у него свои правила использования, и в наборе однотонных
 * штриховых иконок он смотрелся бы наклейкой. Смысл строки несёт подпись рядом.
 */
export function SbpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="6" y="2.5" width="12" height="19" rx="2.4" />
      <rect x="9" y="7" width="2.6" height="2.6" rx="0.6" />
      <rect x="12.4" y="7" width="2.6" height="2.6" rx="0.6" />
      <rect x="9" y="10.4" width="2.6" height="2.6" rx="0.6" />
      <path d="M12.4 13.8h2.6M12.4 10.4v3.4" />
    </Icon>
  );
}
